if (!window.productCardSwatchesLoaded) {
  window.productCardSwatchesLoaded = true;

  const CARD_SELECTOR = '.product-card-wrapper';

  const getCard = (element) => element?.closest(CARD_SELECTOR);

  const getVariants = (card) => {
    if (!card) return [];
    if (card.cardVariants) return card.cardVariants;

    const variantData = card.querySelector('[data-card-variant-data]');
    if (!variantData) {
      card.cardVariants = [];
      return card.cardVariants;
    }

    try {
      card.cardVariants = JSON.parse(variantData.textContent);
    } catch (error) {
      card.cardVariants = [];
    }

    return card.cardVariants;
  };

  const getVariantOption = (variant, position) => {
    if (!variant) return '';
    return variant.options?.[position - 1] || variant[`option${position}`] || '';
  };

  const getVariantInput = (card) => card.querySelector('input.product-variant-id[name="id"]');

  const getCurrentVariant = (card) => {
    const variants = getVariants(card);
    const variantInput = getVariantInput(card);
    const selectedVariantId = variantInput?.value;

    return (
      variants.find((variant) => String(variant.id) === String(selectedVariantId)) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  };

  const getCardQuantity = (card) => {
    const qtyInput = card?.querySelector('[data-card-qty]');
    if (!qtyInput) return 1;

    const min = Number(qtyInput.min);
    const safeMin = Number.isFinite(min) ? min : 0;
    const parsedValue = Number.parseInt(qtyInput.value, 10);

    return Math.max(safeMin, Number.isFinite(parsedValue) ? parsedValue : safeMin);
  };

  const getFormattedPriceParts = (cents, currencyCode) => {
    const amount = Math.max(0, Number(cents) || 0) / 100;
    const locale = document.documentElement.lang || 'en-US';

    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const parts = formatter.formatToParts(amount);
      const symbol = parts.find((part) => part.type === 'currency')?.value || '$';
      const dollars = parts
        .filter((part) => part.type === 'integer' || part.type === 'group')
        .map((part) => part.value)
        .join('');
      const fraction = parts.find((part) => part.type === 'fraction')?.value || '00';

      return {
        symbol,
        dollars,
        fraction,
        accessible: formatter.format(amount),
      };
    } catch (error) {
      const fallbackAmount = amount.toFixed(2);
      const [dollars, fraction = '00'] = fallbackAmount.split('.');

      return {
        symbol: '$',
        dollars,
        fraction,
        accessible: `$${fallbackAmount}`,
      };
    }
  };

  const setPrice = (card, variant) => {
    const price = card?.querySelector('[data-card-price]');
    if (!price || !variant) return;

    const quantity = Math.max(getCardQuantity(card), 1);
    const totalCents = Number(variant.price || 0) * quantity;
    const priceParts = getFormattedPriceParts(totalCents, price.dataset.currencyCode);

    const symbol = price.querySelector('[data-card-price-symbol]');
    const dollars = price.querySelector('[data-card-price-dollars]');
    const cents = price.querySelector('[data-card-price-cents]');
    const accessible = price.querySelector('[data-card-price-accessible]');

    if (symbol) symbol.textContent = priceParts.symbol;
    if (dollars) dollars.textContent = priceParts.dollars;
    if (cents) cents.textContent = priceParts.fraction;
    if (accessible) accessible.textContent = priceParts.accessible;
  };

  const getSelectedOptions = (card) => {
    const currentVariant = getCurrentVariant(card);
    const selectedOptions = currentVariant?.options ? [...currentVariant.options] : [];
    const selectedPositions = new Set();

    card.querySelectorAll('[data-card-option-select]').forEach((select) => {
      const position = Number(select.dataset.optionPosition);
      if (position) {
        selectedPositions.add(position);
        selectedOptions[position - 1] = select.value;
      }
    });

    const activeSwatch = card.querySelector('[data-card-swatch].is-active[data-card-option-position]');
    if (activeSwatch) {
      const position = Number(activeSwatch.dataset.cardOptionPosition);
      if (position && !selectedPositions.has(position)) {
        selectedOptions[position - 1] = activeSwatch.dataset.cardOptionValue || '';
      }
    }

    return selectedOptions;
  };

  const findMatchingVariant = (card, changedControl = null) => {
    const selectedOptions = getSelectedOptions(card);
    const variants = getVariants(card);
    const exactVariant = variants.find((variant) =>
      selectedOptions.every((optionValue, index) => !optionValue || getVariantOption(variant, index + 1) === optionValue)
    );

    if (exactVariant) return exactVariant;

    const changedPosition = Number(changedControl?.dataset.optionPosition || changedControl?.dataset.cardOptionPosition);
    const changedValue = changedControl?.value || changedControl?.dataset.cardOptionValue;

    if (!changedPosition || !changedValue) return null;

    return (
      variants.find((variant) => variant.available && getVariantOption(variant, changedPosition) === changedValue) ||
      variants.find((variant) => getVariantOption(variant, changedPosition) === changedValue)
    );
  };

  const getCompactOptionLabel = (label, optionName = '') => {
    const fullLabel = String(label || '').trim();
    const normalizedOptionName = String(optionName || '').toLowerCase();

    if (normalizedOptionName.includes('size') || normalizedOptionName.includes('width')) {
      return fullLabel.replace(/\s+in(?:ch(?:es)?)?\.?$/i, ' In');
    }

    if (normalizedOptionName.includes('length')) {
      return fullLabel
        .replace(/\s+(?:yards?|yds?\.?)$/i, ' Yd')
        .replace(/\s+(?:feet|foot|ft\.?)$/i, ' Ft');
    }

    return (
      fullLabel.replace(
        /\s+(?:inches?|in\.?|yards?|yds?|feet|foot|ft\.?|mils?|millimeters?|mm|centimeters?|cm|meters?|metres?|rolls?|cases?|packs?)$/i,
        ''
      ) || fullLabel
    );
  };

  const rememberSelectLabels = (select) => {
    select.querySelectorAll('option').forEach((option) => {
      if (!option.dataset.fullLabel) option.dataset.fullLabel = option.textContent.trim();
    });
  };

  const getExpandedOptionLabel = (select, option) => {
    const fullLabel = option.dataset.fullLabel || option.textContent.trim();
    const optionName = String(select.dataset.optionName || '').toLowerCase();
    const hasLetters = /[a-z]/i.test(fullLabel);

    if ((optionName.includes('thickness') || optionName.includes('mil')) && !/\bmil\b/i.test(fullLabel)) {
      return `${fullLabel} Mil`;
    }

    if ((optionName.includes('size') || optionName.includes('width')) && !hasLetters) {
      return `${fullLabel} inches`;
    }

    if (optionName.includes('size') || optionName.includes('width')) {
      return fullLabel.replace(/\s+in(?:ch(?:es)?)?\.?$/i, ' inches');
    }

    return fullLabel;
  };

  const expandCardSelect = (select) => {
    rememberSelectLabels(select);
    select.querySelectorAll('option').forEach((option) => {
      const expandedLabel = getExpandedOptionLabel(select, option);
      option.textContent = expandedLabel;
      option.label = expandedLabel;
    });
  };

  const collapseCardSelect = (select) => {
    rememberSelectLabels(select);
    const selectedOption = select.selectedOptions?.[0];
    if (selectedOption) {
      const compactLabel =
        selectedOption.dataset.compactLabel ||
        getCompactOptionLabel(selectedOption.dataset.fullLabel, select.dataset.optionName);
      selectedOption.textContent = compactLabel;
      selectedOption.label = compactLabel;
    }
  };

  const setCardImage = (card, imageSrc, imageSrcset, imageAlt) => {
    const image = card.querySelector('[data-card-image]');
    if (!image || !imageSrc) return;

    image.src = imageSrc;
    if (imageSrcset) {
      image.srcset = imageSrcset;
    } else {
      image.removeAttribute('srcset');
    }
    if (imageAlt) image.alt = imageAlt;
  };

  const setVariantImage = (card, variant) => {
    const featuredImage = variant?.featured_image || variant?.featured_media?.preview_image;
    const imageSrc = featuredImage?.src || featuredImage?.url;
    const imageAlt = featuredImage?.alt || '';

    setCardImage(card, imageSrc, null, imageAlt);
  };

  const setProductLinks = (card, variant) => {
    const productUrl = card.dataset.cardProductUrl;
    if (!productUrl || !variant?.id) return;

    card.querySelectorAll('[data-card-product-link]').forEach((link) => {
      link.href = `${productUrl}?variant=${variant.id}`;
    });
  };

  const setAddButton = (card, variant) => {
    const variantInput = getVariantInput(card);
    const addButton = card.querySelector('button.card__add-to-cart-button');
    const addButtonText = addButton?.querySelector('span:not(.sold-out-message)');
    const variantIsAvailable = Boolean(variant?.available);
    const quantityIsValid = getCardQuantity(card) > 0;
    const canAddVariant = variantIsAvailable && quantityIsValid;

    if (variantInput && variant?.id) {
      variantInput.value = variant.id;
      variantInput.disabled = !canAddVariant;
    }

    if (!addButton) return;

    addButton.disabled = !canAddVariant;
    addButton.toggleAttribute('aria-disabled', !canAddVariant);

    if (addButtonText) {
      addButtonText.textContent = variantIsAvailable
        ? addButton.dataset.addToCartText || addButtonText.textContent
        : addButton.dataset.soldOutText || addButtonText.textContent;
    }
  };

  const setControls = (card, variant) => {
    if (!variant) return;

    card.querySelectorAll('[data-card-option-select]').forEach((select) => {
      const optionValue = getVariantOption(variant, Number(select.dataset.optionPosition));
      if (optionValue && select.value !== optionValue) select.value = optionValue;
      if (isRelatedProductCard(card)) collapseCardSelect(select);
    });

    card.querySelectorAll('[data-card-swatch][data-card-option-position]').forEach((swatch) => {
      const optionValue = getVariantOption(variant, Number(swatch.dataset.cardOptionPosition));
      const isActive = optionValue === swatch.dataset.cardOptionValue;

      swatch.classList.toggle('is-active', isActive);
      if (isActive) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });
  };

  const syncVariant = (card, preferredImageSource = null, changedControl = preferredImageSource) => {
    const variant = findMatchingVariant(card, changedControl);
    if (!variant) return;

    setAddButton(card, variant);
    setProductLinks(card, variant);
    setControls(card, variant);
    setPrice(card, variant);

    if (preferredImageSource?.dataset.imageSrc) {
      setCardImage(
        card,
        preferredImageSource.dataset.imageSrc,
        preferredImageSource.dataset.imageSrcset,
        preferredImageSource.dataset.imageAlt
      );
    } else {
      setVariantImage(card, variant);
    }
  };

  const syncQuantity = (qtyInput) => {
    const card = getCard(qtyInput);
    if (!card) return;

    const min = Number(qtyInput.min);
    const safeMin = Number.isFinite(min) ? min : 0;
    const parsedValue = Number.parseInt(qtyInput.value, 10);
    const value = Math.max(safeMin, Number.isFinite(parsedValue) ? parsedValue : safeMin);
    const formQtyInput = card.querySelector('.card__add-to-cart-form input[name="quantity"]');

    qtyInput.value = value;
    if (formQtyInput) formQtyInput.value = value;

    const variant = getCurrentVariant(card);
    setPrice(card, variant);
    setAddButton(card, variant);
  };

  const isRelatedProductCard = (card) =>
  Boolean(
    card?.closest(
      '.related-products--product-column, .cart-drawer__related-products'
    )
  );

  const isCollectionProductCard = (card) => Boolean(card?.closest('[data-collection-product-grid]'));

  const initializeCard = (card) => {
    if (!card || card.dataset.cardControlsReady === 'true') return;
    card.dataset.cardControlsReady = 'true';

    const qtyInput = card.querySelector('[data-card-qty]');
    if (qtyInput && (isRelatedProductCard(card) || isCollectionProductCard(card))) {
      qtyInput.value = 1;
      syncQuantity(qtyInput);
    }

    if (isRelatedProductCard(card)) {
      card.querySelectorAll('[data-card-option-select]').forEach(collapseCardSelect);
    }

    const variant = getCurrentVariant(card);
    if (!variant) return;
    setPrice(card, variant);
    setAddButton(card, variant);
  };

  const setRelatedCardQuantity = (card, quantity) => {
    const qtyInput = card?.querySelector('[data-card-qty]');
    if (!qtyInput) return;

    const safeQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
    if (safeQuantity > 0) {
      qtyInput.value = safeQuantity;
      card.classList.add('is-in-cart');
    } else {
      qtyInput.value = 1;
      card.classList.remove('is-in-cart');
    }
    syncQuantity(qtyInput);
  };

  const updateRelatedCartQuantity = async (card, requestedQuantity) => {
    const variant = getCurrentVariant(card);
    if (!variant?.id || card.classList.contains('is-updating')) return;

    card.classList.add('is-updating');
    try {
      const response = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: String(variant.id),
          quantity: Math.max(0, requestedQuantity),
        }),
      });
      const cartData = await response.json();
      if (!response.ok || cartData.status) throw new Error(cartData.description || 'Unable to update cart');

      const updatedItem = cartData.items?.find((item) => String(item.variant_id) === String(variant.id));
      const updatedQuantity = updatedItem?.quantity || 0;

      document.querySelectorAll(
  '.related-products--product-column .product-card-wrapper, .cart-drawer__related-products .product-card-wrapper').forEach((relatedCard) => {
        if (String(getCurrentVariant(relatedCard)?.id) === String(variant.id)) {
          setRelatedCardQuantity(relatedCard, updatedQuantity);
        }
      });

      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-card-stepper',
        cartData,
        variantId: variant.id,
      });
    } catch (error) {
      console.error(error);
      setRelatedCardQuantity(card, getCardQuantity(card));
    } finally {
      card.classList.remove('is-updating');
    }
  };

  document.querySelectorAll(CARD_SELECTOR).forEach(initializeCard);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(CARD_SELECTOR)) initializeCard(node);
        node.querySelectorAll?.(CARD_SELECTOR).forEach(initializeCard);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form' || !event.productVariantId) return;

      document.querySelectorAll(
  '.related-products--product-column .product-card-wrapper, .cart-drawer__related-products .product-card-wrapper').forEach((card) => {
        if (String(getCurrentVariant(card)?.id) !== String(event.productVariantId)) return;
        setRelatedCardQuantity(card, event.cartData?.quantity || 1);
      });
    });
  }

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-swatch]');
    if (swatch) {
      event.preventDefault();
      event.stopPropagation();

      const card = getCard(swatch);
      if (!card) return;

      if (isRelatedProductCard(card)) setRelatedCardQuantity(card, 0);

      const swatchPosition = Number(swatch.dataset.cardOptionPosition);
      if (swatchPosition) {
        const matchingSelect = card.querySelector(
          `[data-card-option-select][data-option-position="${swatchPosition}"]`
        );
        if (matchingSelect) matchingSelect.value = swatch.dataset.cardOptionValue || '';
      }

      card.querySelectorAll('[data-card-swatch].is-active').forEach((activeSwatch) => {
        activeSwatch.classList.remove('is-active');
        activeSwatch.removeAttribute('aria-current');
      });

      swatch.classList.add('is-active');
      swatch.setAttribute('aria-current', 'true');
      syncVariant(card, swatch);
      return;
    }

    const qtyButton = event.target.closest('[data-qty-minus], [data-qty-plus]');
    if (qtyButton) {
      event.preventDefault();
      event.stopPropagation();

      const card = getCard(qtyButton);
      const qtyInput = card?.querySelector('[data-card-qty]');
      if (!qtyInput) return;

      const currentValue = getCardQuantity(card);
      const nextValue = qtyButton.hasAttribute('data-qty-minus') ? currentValue - 1 : currentValue + 1;

      if (isRelatedProductCard(card) && card.classList.contains('is-in-cart')) {
        updateRelatedCartQuantity(card, nextValue);
      } else {
        qtyInput.value = nextValue;
        syncQuantity(qtyInput);
      }
    }
  });

  document.addEventListener(
    'pointerdown',
    (event) => {
      const optionSelect = event.target.closest(
  '.related-products--product-column [data-card-option-select], .cart-drawer__related-products [data-card-option-select]'
);
      if (optionSelect) expandCardSelect(optionSelect);
    },
    true
  );

  document.addEventListener(
    'focusout',
    (event) => {
      const optionSelect = event.target.closest(
  '.related-products--product-column [data-card-option-select], .cart-drawer__related-products [data-card-option-select]'
);
      if (optionSelect) collapseCardSelect(optionSelect);
    },
    true
  );

  document.addEventListener('change', (event) => {
    const optionSelect = event.target.closest('[data-card-option-select]');
    if (!optionSelect) return;

    event.stopPropagation();

    const card = getCard(optionSelect);
    if (card) {
      if (isRelatedProductCard(card)) setRelatedCardQuantity(card, 0);
      syncVariant(card, null, optionSelect);
      collapseCardSelect(optionSelect);
    }
  });

  document.addEventListener('input', (event) => {
    const qtyInput = event.target.closest('[data-card-qty]');
    if (!qtyInput) return;

    event.stopPropagation();
    syncQuantity(qtyInput);
  });
}
